import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import { API } from '../config/api';

const GalleryContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 6rem 2rem 2rem 2rem;
`;

const GalleryHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const GalleryTitle = styled.h1`
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: 900;
  background: linear-gradient(45deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
`;

const GallerySubtitle = styled.p`
  font-size: 1.2rem;
  color: #636e72;
  line-height: 1.6;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const GalleryCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const GalleryImage = styled.div`
  width: 100%;
  height: 350px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      transparent 70%,
      rgba(0, 0, 0, 0.7) 100%
    );
  }
`;

const GalleryCardContent = styled.div`
  padding: 1.5rem;
`;

const BookTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: #2d3436;
  margin-bottom: 0.5rem;
`;

const BookGenre = styled.span`
  display: inline-block;
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const ViewBookButton = styled(motion.button)`
  background: linear-gradient(45deg, #f093fb, #f5576c);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  font-size: 0.9rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(240, 147, 251, 0.4);
  }
`;

// Modal/Lightbox Styles
const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 20px;
  max-width: 90vw;
  max-height: 90vh;
  width: 700px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  position: relative;
  
  @media (max-width: 768px) {
    max-height: 95vh;
    width: 95vw;
  }
`;

const ModalImage = styled.img`
  width: 100%;
  height: 60vh;
  object-fit: cover;
  display: block;
  
  @media (max-width: 768px) {
    height: 50vh;
  }
`;

const ModalInfo = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  z-index: 10;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  font-size: 1.2rem;
  color: #636e72;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #636e72;
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #2d3436;
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.6;
  }
`;

const Gallery = () => {
  const navigate = useNavigate();
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchGalleryImages();
  }, []);

  const fetchGalleryImages = async () => {
    try {
      const response = await fetch(`${API}/gallery`);
      if (response.ok) {
        const galleryImages = await response.json();
        const allGalleryItems = galleryImages.map((image, index) => ({
          id: image.id,
          image: image.imagePath,
          bookId: image.bookId,
          bookTitle: image.bookTitle,
          bookGenre: image.bookGenre,
          bookDescription: image.description || image.bookDescription,
          bookSlug: image.bookTitle ? image.bookTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') : 'unknown'
        }));
        
        setGalleryItems(allGalleryItems);
      }
    } catch (error) {
      console.error('Error fetching gallery images:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (item) => {
    setSelectedImage(item);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const navigateToBook = (bookId, bookSlug) => {
    navigate(`/book/${bookId}/${bookSlug}`);
  };

  if (loading) {
    return (
      <GalleryContainer>
        <LoadingContainer>Loading character gallery...</LoadingContainer>
      </GalleryContainer>
    );
  }

  return (
    <GalleryContainer>
      <GalleryHeader
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <GalleryTitle>Character Gallery</GalleryTitle>
        <GallerySubtitle>
          Explore the characters and worlds from Winter North's captivating stories. 
          Click any image for a closer look, or visit the book page to learn more.
        </GallerySubtitle>
      </GalleryHeader>

      {galleryItems.length > 0 ? (
        <GalleryGrid>
          {galleryItems.map((item, index) => (
            <GalleryCard
              key={item.id}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <GalleryImage 
                src={`${API}${item.image}`} 
                alt={item.title} 
                onClick={() => openModal(item)}
              />
              <GalleryCardContent>
                <BookTitle>{item.bookTitle}</BookTitle>
                <BookGenre>{item.bookGenre || 'Fiction'}</BookGenre>
                <ViewBookButton
                  onClick={() => navigateToBook(item.bookId, item.bookSlug)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  View Book Details
                </ViewBookButton>
              </GalleryCardContent>
            </GalleryCard>
          ))}
        </GalleryGrid>
      ) : (
        <EmptyState>
          <h3>No Gallery Images Yet</h3>
          <p>
            Gallery images will appear here once you add character or promotional images 
            to your books through the admin panel.
          </p>
        </EmptyState>
      )}

      {/* Image Modal/Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <ModalContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseButton onClick={closeModal}>×</CloseButton>
              <ModalImage 
                src={`${API}${selectedImage.image}`} 
                alt={selectedImage.title} 
              />
              <ModalInfo>
                <BookTitle>{selectedImage.bookTitle}</BookTitle>
                {selectedImage.bookDescription && (
                  <p style={{ 
                    color: '#636e72', 
                    fontSize: '1rem', 
                    lineHeight: '1.6', 
                    margin: '1rem 0',
                    textAlign: 'left'
                  }}>
                    {selectedImage.bookDescription}
                  </p>
                )}
                <BookGenre>{selectedImage.bookGenre || 'Fiction'}</BookGenre>
                <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center' }}>
                  <ViewBookButton
                    onClick={() => {
                      closeModal();
                      navigateToBook(selectedImage.bookId, selectedImage.bookSlug);
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    View Book Details
                  </ViewBookButton>
                </div>
              </ModalInfo>
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>
    </GalleryContainer>
  );
};

export default Gallery;
